
import React from "react";
import PropTypes from "prop-types";

function getContributionKey(contribution, index) {
  if (
    contribution &&
    typeof contribution === "object" &&
    typeof contribution.id === "string" &&
    contribution.id.trim() !== ""
  ) {
    return contribution.id;
  }

  return `studio-contribution-${index}`;
}

function renderContribution(contribution, context) {
  if (contribution === null || contribution === undefined) {
    return null;
  }

  if (React.isValidElement(contribution)) {
    return contribution;
  }

  if (
    typeof contribution === "string" ||
    typeof contribution === "number"
  ) {
    return contribution;
  }

  if (typeof contribution === "function") {
    return contribution(context);
  }

  if (typeof contribution === "object") {
    if (React.isValidElement(contribution.node)) {
      return contribution.node;
    }

    if (typeof contribution.render === "function") {
      return contribution.render(context);
    }

    if (contribution.component) {
      const ContributionComponent = contribution.component;

      return (
        <ContributionComponent
          {...(contribution.props || {})}
          studioContribution={contribution}
          studioContributionContext={context}
        />
      );
    }
  }

  return null;
}

/**
 * Materializes Runtime contributions without coupling StudioRuntime
 * to React or to a business domain.
 */
export default function StudioContributionSlot({
  type,
  contributions = [],
  context = {},
  className = "",
}) {
  if (!Array.isArray(contributions)) {
    throw new TypeError(
      "StudioContributionSlot.contributions must be an array."
    );
  }

  const renderedContributions = contributions
    .map((contribution, index) => {
      const rendered = renderContribution(contribution, {
        ...context,
        type,
        contribution,
        index,
      });

      if (rendered === null || rendered === undefined) {
        return null;
      }

      return (
        <React.Fragment key={getContributionKey(contribution, index)}>
          {rendered}
        </React.Fragment>
      );
    })
    .filter(Boolean);

  if (renderedContributions.length === 0) {
    return null;
  }

  return (
    <div
      className={[
        "studio-runtime-contributions",
        `studio-runtime-contributions--${type}`,
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      data-studio-contribution-type={type}
    >
      {renderedContributions}
    </div>
  );
}

StudioContributionSlot.propTypes = {
  type: PropTypes.string.isRequired,
  contributions: PropTypes.array,
  context: PropTypes.object,
  className: PropTypes.string,
};

export {
  getContributionKey,
  renderContribution,
};
